import { gql, useMutation, useQuery } from '@apollo/client'
import { Button, Checkbox, Col, Divider, Form, Input, message, Modal, ModalProps, Row, Typography } from 'antd'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { useForm } from 'antd/lib/form/Form'
import dayjs from 'dayjs'
import React, { Fragment, useEffect, useState } from 'react'
import styled from 'styled-components'
import { queryCache, useDemandeUpdate } from '../../apollo/hooks'
import { DEMANDE_DEVIS_FOURNISSEURS_MUTATION } from '../../apollo/mutation/demandedevisfournisseur.mutation'
import { EVENEMENT_ADD_MUTATION } from '../../apollo/mutation/evenement.mutation'
import { FICHIER_QUERY } from '../../apollo/mutation/fichier.mutation'
import { EVENEMENTS } from '../../apollo/query/demande.query'
import { C_S_QUERY } from '../../apollo/query/personne.query'
import { AWS_S3_URL, AWS_S3_RESIZED_URL, COLOR, DATE_FORMAT } from '../../constants'
import { formatAddress, formatMembreCSOptions, httpRequest, validateEmail } from '../../helper'
import { useAuth, useUpdateKanban } from '../../lib/hooks'
import {
  D4_Conseilsyndical,
  D4_Demandedevis,
  D4_Demandedevisfournisseur,
  D4_Demandedevisfournisseur_Insert_Input,
  D4_Demandedevis_Insert_Input,
  D4_Evenementdemande_Insert_Input,
  D4_Fichier,
  D4_Fn_View_Demande,
  D4_Fn_View_Evenements,
  D4_Personne,
  D4_Societe,
} from '../../types'
import MSGraphAPI from '../Courriel/ms-graph.api'
import D4SelectSociete from '../D4SelectSociete'
import { DatePicker } from '../DatePicker'
import SelectCoproprietaires from '../SelectCoproprietaires'
import Thumbnail from '../Thumbnail'

const DEMANDE_DEVIS_COUNT_QUERY = gql`
  query DemandeDevisCount($demandeId: bigint!) {
    d4_demandedevis_aggregate(where: { demande_id: { _eq: $demandeId } }) {
      aggregate {
        count
      }
    }
  }
`

const DEMANDE_DE_DEVIS_MUTATION = gql`
  mutation DemandeDeDevis($data: d4_demandedevis_insert_input!) {
    insert_d4_demandedevis_one(object: $data) {
      id
      demande_id
      reference
      objet
      lieuintervention
      datedemandedevis
    }
  }
`

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
}

const tailLayout = {
  wrapperCol: { offset: 22 },
}

let isPdf = (texte: string) => /\.(pdf)$/i.test(texte ?? '')

interface ModelMailProps {
  copro: string
  objet: string
  reference: string
  gestionnaire: string
  contactsSurPlace: D4_Personne[] | undefined
  lienUploadDevis: string
  checked: D4_Fn_View_Evenements[]
}

const modelMail = ({ copro, objet, reference, gestionnaire, contactsSurPlace, lienUploadDevis, checked }: ModelMailProps) => `

Bonjour,</br></br>
Veuillez trouver ci-joint une demande de devis pour la copropriété citée en objet.</br></br>
Adresse: ${copro}</br></br>
Objet: ${objet.replaceAll('\n', '</br>')}</br></br>
Contacts sur place :</br>
${
  contactsSurPlace
    ?.map((contact) => `${contact?.nom} ${contact?.prenom} - ${contact?.telportable} ${contact?.telfixe ?? ''}`)
    .join('</br>') ?? 'Aucun'
}</br></br>

Merci de rappeler la référence suivante sur le devis: ${reference}</br></br>

<div style="padding: 5px; border: 1px solid orange;">
Nous vous remercions de bien vouloir nous faire parvenir votre proposition directement via ce lien : <a href="${lienUploadDevis}" target="__blank">Envoyer le devis</a></br></br>
Envoyer votre devis via ce lien permet d'informer les conseils syndicaux en temps réel et d'accélérer les traitements des dossiers en cours.</br></br>
En cas de problème, vous avez la possibilité de nous faire parvenir votre proposition en répondant à ce mail. Cette possibilité reste active jusqu'au 30/06.</br></br>
Passée cette date, l'envoi des devis se fera obligatoirement via le lien ci dessus.</br></br>
Compte tenu du nombre important de devis que nous recevons, les devis reçus en dehors de cette procédure ne seront potentiellement pas traités.
</div>
</br></br>
Sincères salutations,</br></br>
${checked
  ?.map(
    (ev) =>
      `<div>${ev.auteurnom} - ${ev.auteurprenom}<br />${
        ev.fichier
          ? `<a href="${AWS_S3_URL}${ev.texte}" target="_blank" >
        <img width="150px" src="${AWS_S3_RESIZED_URL}resized-${ev.texte}${
              isPdf(ev.texte ?? '') ? '.png' : ''
            }" />
      </a>`
          : ev.texte
      }</div><hr><br />`
  )
}</br></br>
${gestionnaire}
`

const DDevForm = ({
  fichierId,
  demande,
  onSubmit,
}: {
  fichierId?: number
  demande: D4_Fn_View_Demande
  onSubmit: any
}) => {
  const [form] = useForm()
  const [loading, setLoading] = useState(false)
  const [createDemandeDevis] = useMutation<
    { insert_d4_demandedevis_one: D4_Demandedevis },
    { data: D4_Demandedevis_Insert_Input }
  >(DEMANDE_DE_DEVIS_MUTATION)
  const [createEvenementDemande] = useMutation<{}, { data: D4_Evenementdemande_Insert_Input }>(EVENEMENT_ADD_MUTATION)
  const [createDemandeDevisFournisseurs] = useMutation<
    { ddev: D4_Demandedevisfournisseur[] },
    { data: D4_Demandedevisfournisseur_Insert_Input[] }
  >(DEMANDE_DEVIS_FOURNISSEURS_MUTATION)
  const { data: cs } = useQuery<{ d4_conseilsyndical: D4_Conseilsyndical[] }, { trg: string }>(C_S_QUERY, {
    variables: { trg: demande.copropriete?.trigramme ?? '' },
  })
  const { data: count } = useQuery(DEMANDE_DEVIS_COUNT_QUERY, {
    variables: { demandeId: demande.id },
  })
  const [updateDemande] = useDemandeUpdate()
  const {
    user: {
      personne: { nom, prenom },
    },
  } = useAuth()

  const { data: fichier } = useQuery<{ fichier: D4_Fichier; id: number }>(FICHIER_QUERY, {
    variables: { id: Number(fichierId) },
    skip: !fichierId,
  })

  const { updateKanban } = useUpdateKanban(demande.id)

  const {
    description,
    batimentlibelle,
    equipementlibelle,
    copropriete: {
      //@ts-expect-error
      trigramme,
      //@ts-expect-error
      personnes,
    },
  } = demande

  // Récupération des éléments checkés

  var checked: any[] = []

  const verifCheck = (e: CheckboxChangeEvent, evenements: D4_Fn_View_Evenements) => {

    if(e.target.checked === true){
      checked[checked.length] = evenements
    }
    else{
      checked.forEach(element => {
        checked = checked.filter(e => e !== evenements)
      })
    }

    return checked
  }

  //

  const [membresCsOptions, membreCsValues] = cs?.d4_conseilsyndical
    ? formatMembreCSOptions(cs?.d4_conseilsyndical ?? [], 'id')
    : []

  const contactsOptions = [
    {
      key: 'concierges',
      label: 'Concierge(s)',
      options: personnes.map((p: any) => ({
        label: `(Concierge) ${p?.nom} ${p?.prenom}`,
        value: p.id,
      })),
    },
  ]
    //@ts-expect-error
    .concat(membresCsOptions)
    .filter((v) => v)

  const onFinish = async (values: any) => {
    setLoading(true)

    try {
      const societes = values.fournisseur_id.map((fournisseur: number) => {
        const societe = queryCache<D4_Societe>({
          typename: 'd4_societe',
          id: fournisseur,
          //@ts-expect-error
          fields: ['mail1', `raisonsociale`],
        })
        if (!validateEmail(societe.mail1 ?? '')) {
          throw new Error(societe.mail1 + ' Email invalide')
        }
        return societe
      })

      const data: D4_Demandedevis_Insert_Input = {
        datedemandedevis: dayjs(),
        objet: values.objet,
        lieuintervention: values.lieuintervention,
        reference: values.reference,
        demande_id: demande.id,
      }

      if (values.contacts) {
        data.contacts = {
          data: values?.contacts?.map((contact: number) => ({ contactssurplace_id: contact })),
        }
      }

      const contactsSurPlace = values?.contacts?.map((contact: number) =>
        personnes.concat(membreCsValues).find((p: D4_Personne) => p.id === Number(contact))
      )

      /**
       * Si la demande est déjà en cours de traitement, on ne crée pas de nouvelle demande de devis
       */
      let ddevId = fichier?.fichier.devis_fournisseur?.demandedevisfournisseur?.demandedevis_id

      if (!ddevId) {
        const ddev = await createDemandeDevis({ variables: { data } })
        ddevId = ddev.data?.insert_d4_demandedevis_one.id
      }

      const ddevFournisseursData = values.fournisseur_id.map((fournisseur: number) => ({
        demandedevis_id: ddevId,
        fournisseur_id: fournisseur,
        daterappel: values.daterappel,
      }))

      const res = await createDemandeDevisFournisseurs({
        variables: {
          data: ddevFournisseursData,
        },
      })

      await httpRequest(`https://vision.d4immobilier.fr/GenererFichiersDDev?id=${ddevId}`)

      // @ts-expect-error
      const ddevFournisseurs = res.data?.ddev?.returning ?? []

      await Promise.all(
        societes.map((societe: D4_Societe) => {
          const email = {
            message: {
              subject: `${demande.copropriete?.nom} / Demande de devis (${demande.id})`,
              body: {
                contentType: 'html',
                content: modelMail({
                  copro: values.lieuintervention,
                  gestionnaire: `${prenom} ${nom}`,
                  objet: values.objet,
                  reference: values.reference,
                  contactsSurPlace,
                  lienUploadDevis: `https://vision.d4immobilier.fr/fournisseurs/detail_ddev.jsp?id=${
                    ddevFournisseurs.find((d: D4_Demandedevisfournisseur) => d.fournisseur_id === societe.id)?.id
                  }`,
                  checked: checked
                }),
              },
              toRecipients: [{ emailAddress: { address: societe.mail1 } }],
              // attachments: fichiers,
            },
            saveToSentItems: 'true',
          }

          return MSGraphAPI.sendEmail({ data: email })
        })
      )

      await updateKanban(3768204, { label: '', value: 3768204, etatkanbanid: 3768204 })

      await createEvenementDemande({
        variables: {
          data: {
            demande_id: demande.id,
            texte: `Demande de devis envoyée à ${societes
              .map((societe: D4_Societe) => societe.raisonsociale)
              .join(', ')} ${values.reference}`,
          },
        },
        refetchQueries: [
          {
            query: EVENEMENTS,
            variables: { id: demande.id },
          },
        ],
      })
      updateDemande({
        variables: { id: demande.id, data: { datedernierimportmailatraiter: null } },
      })

      form.resetFields()
      setLoading(false)
      onSubmit()
    } catch (error) {
      setLoading(false)
      message.error((error as Error).toString())
    }
  }
  useEffect(() => {
    if (!fichier) {
      const reference = `${dayjs().format('YYYY-MM-DD')}-${demande.copropriete?.trigramme}-DDev-${
        count?.d4_demandedevis_aggregate?.aggregate?.count + 1
      }${demande.batimentlibelle === 'Général' ? '' : '-' + demande.batimentlibelle}`

      form.setFieldsValue({
        lieuintervention: formatAddress(demande.copropriete as any),
        demande_id: demande.id,
        dateaccordcs: null,
        daterappel: dayjs().add(10, 'day'),
        objet: `${trigramme} > ${batimentlibelle} > ${equipementlibelle} > ${description} \n\nEn nous faisant parvenir votre devis sous 20 jours, vous serez assuré qu'il sera présenté à nos clients. 
        S'il vous est difficile de nous le transmettre dans ce délai, nous prévenir permettra de prendre des dispositions ou de valider un nouveau délai.`,
        reference,
      })
    } else {
      const demandedevis = fichier?.fichier.devis_fournisseur?.demandedevisfournisseur?.demandedevis

      form.setFieldsValue({
        lieuintervention: demandedevis?.lieuintervention,
        demande_id: demandedevis?.demande_id,
        dateaccordcs: null,
        daterappel: dayjs().add(10, 'day'),
        objet: demandedevis?.objet,
        reference: demandedevis?.reference,
        contacts: demandedevis?.contacts?.map((contact: any) => contact.contactssurplace_id),
      })
    }
  }, [
    batimentlibelle,
    count?.d4_demandedevis_aggregate?.aggregate?.count,
    demande,
    description,
    equipementlibelle,
    fichier,
    fichierId,
    form,
    trigramme,
  ])

  return (
    <Form
      form={form}
      {...layout}
      onFinish={onFinish}
      layout="vertical"
      initialValues={{ contacts: personnes.map((p: D4_Personne) => p.id) }}
    >
      <Row>
        <Col span={8}>
          <Form.Item name="demande_id" noStyle hidden />
          <Form.Item name="rappel" noStyle hidden />
          <Form.Item label="Copropriété">
            <Typography.Text>{trigramme}</Typography.Text>
          </Form.Item>
          <Form.Item name="reference" label="Référence">
            <Input disabled={!!fichierId} />
          </Form.Item>
          <Form.Item name="coproprietaire" label="Copropriétaire">
            <SelectCoproprietaires
              trigramme={trigramme}
              onChange={(value, option) => {
                form.setFieldsValue({
                  coproprietaire: value,
                  rappel: `${
                    option.lot_coproprietaire?.coproprietaire?.personne
                      ? `${option.lot_coproprietaire?.coproprietaire?.personne?.nom} ${option.lot_coproprietaire?.coproprietaire?.personne?.prenom}`
                      : option.lot_coproprietaire?.coproprietaire?.societe?.raisonsociale
                  }`,
                })
              }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="fournisseur_id" label="Fournisseur">
            <D4SelectSociete mode="tags" />
          </Form.Item>
          <Form.Item name="objet" label="Objet">
            <Input.TextArea rows={5} />
          </Form.Item>
          <Form.Item name="lieuintervention" label="Lieu">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="contacts" label="Contacts">
            <SelectCoproprietaires
              mode="tags"
              trigramme={trigramme}
              options={contactsOptions}
              onChange={(value) => {
                form.setFieldsValue({ contacts: value })
              }}
            />
          </Form.Item>
          <Form.Item name="daterappel" label="Date max avant relance">
            <DatePicker format={DATE_FORMAT} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item {...tailLayout}>
            <Button type="primary" htmlType="submit" disabled={loading} loading={loading}>
              Envoyer
            </Button>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="evt" label="Ajouter des fichiers">
              {demande.evenements
                ?.filter((ev) => ev.fichier)
                ?.map((evt) => (
                  <Fragment key={evt.id}>
                    <Checkbox onChange={event => verifCheck(event, evt)} value={evt.id}>
                      <>
                        {evt.texte}
                        <Thumbnail texte={evt.texte} />
                      </>
                    </Checkbox>
                    <Divider />
                  </Fragment>
                ))}
          </Form.Item>
        </Col>
      </Row>
      <Form.Item {...tailLayout}>
        <Button type="primary" htmlType="submit" disabled={loading} loading={loading}>
          Envoyer
        </Button>
      </Form.Item>
    </Form>
  )
}

const ModalWrapper = styled(Modal)`
  .ant-modal-close-icon {
    color: white;
  }
  .ant-modal-header {
    background-color: ${COLOR.green};
    .ant-modal-title {
      color: white;
    }
  }
`

export const DDevModalForm: React.FC<ModalProps & { fichierId?: number; demande: D4_Fn_View_Demande }> = ({
  fichierId,
  demande,
  ...rest
}) => {
  return (
    <ModalWrapper {...rest} title="Demande de devis">
      <DDevForm fichierId={fichierId} demande={demande} onSubmit={(res: any) => rest?.onCancel?.(res)} />
    </ModalWrapper>
  )
}

export default DDevForm
